import { useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Folder,
  Trash2,
  Pencil,
  AlertCircle,
} from "lucide-react";
import { useConfigStore } from "../../../../store/configStore";
import { useEditorStore } from "../../../../store/editorStore";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { layerIcon } from "commons/cdn";
import { LayerIconPicker } from "../../../../components/LayerIconPicker";
import { StaticSortableSkuItem } from "./StaticSortableSkuItem";
import { StaticSkuUploadDialog } from "./StaticSkuUploadDialog";
import { SelectionGroupLabel } from "./SelectionGroupLabel";
import { SelectionRequiredCheckbox } from "./SelectionRequiredCheckbox";
import { Button } from "@/components/ui/button";

import { SelectionData } from "../../../../types/selection";

interface SortableItemProps {
  selection: SelectionData;
}

/** 静态层模版：静态 SKU 网格与上传，无 Skin/调色冲突逻辑 */
export function StaticSortableSelectionItem({ selection }: SortableItemProps) {
  const { templateConfig } = useConfigStore();
  const {
    deleteSelection,
    updateSelectionName,
    updateSelectionLayerIcon,
    reorderSkuInSelection,
    editingSkuId,
    setEditingSku,
    editingSelectionId,
    setEditingSelectionId,
    selectedSkins,
  } = useEditorStore();

  const skuIds = selection.skuIds || [];
  const skus = templateConfig?.skus || {};
  const skuList = useMemo(() => {
    return skuIds.map((skuId: string) => skus[skuId]).filter(Boolean);
  }, [skuIds, skus]);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(selection.name || "");
  const [staticUploadOpen, setStaticUploadOpen] = useState(false);

  const skuSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: selection.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const selectedCount = skuList.filter((sku: any) =>
    selectedSkins.includes(sku.skinName),
  ).length;

  const getSkuErrors = useEditorStore((s) => s.getSkuErrors);
  const orphanCount = skuIds.filter((skuId: string) =>
    getSkuErrors(skuId).some((e) => e.type === "orphan"),
  ).length;

  const dataErrorCount = skuIds.filter((skuId: string) =>
    getSkuErrors(skuId).some(
      (e) => e.type === "invalid_toning" || e.type === "duplicate_toning",
    ),
  ).length;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (skuIds.length === 0) {
      deleteSelection(selection.id);
    }
  };

  const handleNameSave = () => {
    if (nameValue.trim()) {
      updateSelectionName(selection.id, nameValue.trim());
    }
    setEditingName(false);
  };

  const hasError = orphanCount > 0 || dataErrorCount > 0;

  return (
    <>
      <StaticSkuUploadDialog
        mode="create"
        selectionId={selection.id}
        open={staticUploadOpen}
        onOpenChange={setStaticUploadOpen}
      />
      <AccordionItem
        ref={setNodeRef}
        style={style}
        value={selection.id}
        className={cn(
          "group border rounded-lg mb-2 bg-card",
          hasError && "border-red-500 border-2",
          isDragging && "opacity-50 shadow-lg",
        )}
      >
        <AccordionTrigger className="px-3 py-2 hover:no-underline">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div
              {...attributes}
              {...listeners}
              role="button"
              tabIndex={0}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>

            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-mono text-primary shrink-0">
              {selection.playIndex + 1}
            </div>

            <LayerIconPicker
              currentLayerIconId={selection.layerIconId}
              onSelect={(icon) => {
                if (icon) {
                  updateSelectionLayerIcon(selection.id, icon);
                }
              }}
            >
              <div
                role="button"
                tabIndex={0}
                className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground"
                title={
                  selection.layerIconId
                    ? `已绑定 LayerIcon: ${selection.layerIconId}`
                    : "未绑定 LayerIcon，点击选择"
                }
                onClick={(e) => e.stopPropagation()}
              >
                {selection.layerIconId ? (
                  <img
                    src={layerIcon(selection.layerIconId)}
                    alt="LayerIcon"
                    className="h-6 w-6 object-contain"
                  />
                ) : (
                  <Folder className="h-4 w-4 text-red-500" />
                )}
              </div>
            </LayerIconPicker>

            {editingName ? (
              <Input
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onBlur={handleNameSave}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleNameSave();
                  if (e.key === "Escape") {
                    setEditingName(false);
                    setNameValue(selection.name || "");
                  }
                }}
                className="h-6 text-sm w-32"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className="flex items-center gap-1 min-w-0">
                <span className="font-medium text-sm truncate">
                  {selection.name || selection.id}
                </span>
                {selection.name && (
                  <span className="text-xs text-muted-foreground truncate">
                    ({selection.id})
                  </span>
                )}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setNameValue(selection.name || selection.id);
                    setEditingName(true);
                  }}
                  className="p-0.5 hover:bg-muted rounded opacity-0 group-hover:opacity-100 cursor-pointer"
                  title="编辑名称"
                >
                  <Pencil className="h-3 w-3 text-muted-foreground" />
                </div>
              </div>
            )}

            <SelectionGroupLabel gid={selection.gid} />

            <SelectionRequiredCheckbox selection={selection} />

            <div className="flex items-center gap-1 ml-auto mr-2 shrink-0">
              <Badge
                variant={selectedCount > 0 ? "default" : "secondary"}
                className="h-5 px-1.5 text-[10px]"
              >
                {selectedCount}/{skuIds.length}
              </Badge>
              {orphanCount > 0 && (
                <Badge
                  variant="destructive"
                  className="h-5 px-1.5 text-[10px]"
                  title={`${orphanCount} 个 SKU 找不到对应 Skin`}
                >
                  {orphanCount} 无效
                </Badge>
              )}
              {dataErrorCount > 0 && (
                <div
                  className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center"
                  title={`${dataErrorCount} 个 SKU 有数据问题（调色方案引用无效或重复）`}
                >
                  <AlertCircle className="h-3 w-3 text-white" />
                </div>
              )}

              <div
                role="button"
                tabIndex={0}
                className={cn(
                  "h-6 px-2 shrink-0 inline-flex items-center justify-center rounded-md text-xs font-medium cursor-pointer",
                  editingSelectionId === selection.id
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent hover:text-accent-foreground",
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingSelectionId(selection.id);
                }}
              >
                详情
              </div>

              {skuIds.length === 0 && (
                <div
                  role="button"
                  tabIndex={0}
                  className="h-6 w-6 inline-flex items-center justify-center rounded-md text-destructive hover:bg-accent cursor-pointer"
                  onClick={handleDelete}
                  title="删除空 Selection"
                >
                  <Trash2 className="h-3 w-3" />
                </div>
              )}
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-3 pb-3">
          {skuIds.length === 0 ? (
            <div className="text-center py-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                暂无静态 SKU。请先选择图片上传，系统会创建带图 SKU，再在详情中配置价格等。
              </p>
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={() => setStaticUploadOpen(true)}
              >
                添加静态图（可多选）
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <DndContext
                sensors={skuSensors}
                collisionDetection={closestCenter}
                onDragEnd={(event: DragEndEvent) => {
                  const { active, over } = event;
                  if (over && active.id !== over.id) {
                    const oldIndex = skuIds.indexOf(active.id as string);
                    const newIndex = skuIds.indexOf(over.id as string);
                    if (oldIndex !== -1 && newIndex !== -1) {
                      reorderSkuInSelection(selection.id, oldIndex, newIndex);
                    }
                  }
                }}
              >
                <SortableContext items={skuIds} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-3 gap-2">
                    {skuList.map((sku: any) => (
                      <StaticSortableSkuItem
                        key={sku.id}
                        sku={sku}
                        selectionId={selection.id}
                        isEditing={editingSkuId === sku.id}
                        pro={sku.pro}
                        hidden={sku.hidden}
                        onEdit={() => setEditingSku(sku.id, selection.id)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <Button
                size="sm"
                variant="secondary"
                type="button"
                className="w-full"
                onClick={() => setStaticUploadOpen(true)}
              >
                添加静态图（可多选）
              </Button>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </>
  );
}
